import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/structured-logger';

export interface SocialInsight {
  id: string;
  organization_id: string;
  insight_type: 'performance_trend' | 'content_recommendation' | 'timing_optimization' | 
                'engagement_opportunity' | 'dna_drift_warning' | 'competitive_intel';
  priority: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  data: Record<string, any>;
  ai_reasoning?: string;
  action_url?: string;
  status: 'pending' | 'viewed' | 'actioned' | 'dismissed';
  created_at: string;
  expires_at?: string;
}

export interface SmartAction {
  id: string;
  organization_id: string;
  action_type: 'create_campaign' | 'recycle_content' | 'adjust_posting_time' | 
                'update_dna' | 'connect_platform' | 'enhance_content' | 'a_b_test';
  priority: number;
  title: string;
  description: string;
  action_data: Record<string, any>;
  ai_reasoning: string;
  estimated_impact?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'dismissed';
}

/**
 * GrowthSignalsService
 * Your daily social media strategist - surfaces social insights and smart actions
 */
export class GrowthSignalsService {
  private static logger = logger.child({ service: 'GrowthSignals' });

  /**
   * Get pending social insights for an organization
   */
  static async getSocialInsights(organizationId: string): Promise<SocialInsight[]> {
    const { data, error } = await supabase
      .from('social_insights')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('status', 'pending')
      .order('priority', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(10);

    if (error) {
      this.logger.error('Failed to fetch social insights', error, { organizationId });
      throw error;
    }

    // Type assertion: database returns string types but our interface uses specific unions
    return data as SocialInsight[];
  }

  /**
   * Get smart actions for an organization
   */
  static async getSmartActions(organizationId: string): Promise<SmartAction[]> {
    const { data, error } = await supabase
      .from('smart_actions')
      .select('*')
      .eq('organization_id', organizationId)
      .in('status', ['pending', 'in_progress'])
      .order('priority', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(5);

    if (error) {
      this.logger.error('Failed to fetch smart actions', error, { organizationId });
      throw error;
    }

    // Type assertion: database returns string/Json types but our interface uses specific unions/types
    // Cast through unknown first due to structural differences (priority: string vs number, etc.)
    return data as unknown as SmartAction[];
  }

  /**
   * Mark social insight as viewed
   */
  static async markInsightViewed(insightId: string): Promise<void> {
    const { error } = await supabase
      .from('social_insights')
      .update({ status: 'viewed' })
      .eq('id', insightId);

    if (error) {
      this.logger.error('Failed to mark insight viewed', error, { insightId });
      throw error;
    }
  }

  /**
   * Mark smart action as completed
   */
  static async completeAction(actionId: string): Promise<void> {
    const { error } = await supabase
      .from('smart_actions')
      .update({ 
        status: 'completed',
        completed_at: new Date().toISOString()
      })
      .eq('id', actionId);

    if (error) {
      this.logger.error('Failed to complete action', error, { actionId });
      throw error;
    }
  }

  /**
   * Dismiss a social insight or smart action
   */
  static async dismiss(
    type: 'insight' | 'action',
    itemId: string
  ): Promise<void> {
    const table = type === 'insight' ? 'social_insights' : 'smart_actions';
    const updateData = table === 'social_insights' 
      ? { status: 'dismissed' }
      : { status: 'dismissed', dismissed_at: new Date().toISOString() };

    const { error } = await supabase
      .from(table)
      .update(updateData)
      .eq('id', itemId);

    if (error) {
      this.logger.error(`Failed to dismiss ${table} item`, error, { itemId });
      throw error;
    }
  }
}
